import * as React from "react";
import "./styles.scss";
import {
    ProcessStage,
    ProcessStageArrow,
    ProcessStageArrowDirection,
    type ProcessStageOrder,
    useProcessController,
} from "../../../../Processes";
import {VeBoxCard} from "../../../../../components/card/VeBoxCard";
import {Display} from "../../../../../components/Display";
import {motion, type MotionProps} from "framer-motion";
import {AccordionSectionProps, infoIcons} from "@vebox/etui-core";
import {Theme} from "@vebox/etui-themes";
import {EquipmentWithStatusSection} from "../../widgets/EquipmentWithStatusSection";
import {DesAccordion} from "../../../../../components/DesAccordion";
import {useCensusEnums} from "../../data/hooks/CecnsusCoolerControllerHooks";
import {Loader} from "../../../../../components/Utils/loader/Loader";
import {FormCardFooter} from "../../../../common";
import {SuggestedActionsContext} from "../../providers/SuggestedActionsProvider";
import {ManualChange} from "../../data/models/JSONCensusEquipmentsActionsConfirmation";
import {CensusCoolerContext} from "../../providers/CensusCollerProvider";
import {filterOpenedSections, filterReasons, getTableHeadersName, toggleSection,} from "./utils";
import {RestApiError} from "../../data/models/RestApi";
import {VeboxOverlayController} from "../../../../../components/overlay/VeboxOverlay";
import {DefaultErrorBoundaryFallback} from "../../../../../ErrorBoundary/DefaultErrorBoundaryFallback";
import {MassiveOnSubmitModal} from "../../../components/MassiveOnSubmitModal";
import {Section} from "./types";
import {AdviseModal} from "../../components/AdviseModal";
import {EmptyState} from "@vebox/etui-core/EmptyState";

export const accordionStyle = (
    theme: Theme,
    withNestedAccordion: boolean,
    level: number = 0,
    height: string = "520px",
): AccordionSectionProps["styles"] => ({
    sectionContent: {
        height: `100%`,
        maxHeight: height,
        bg: !withNestedAccordion ? theme.palette.common.white : undefined,
        p: 0,
        pl: withNestedAccordion ? 35 * (level + 1) : 0,
        pt: 0.9,
    },
    header: {
        title: {
            text: {
                fontWeight: "bold",
            },
        },
        root: {
            bg: theme.palette.grey[40],
        },
    },
});

interface CensusStatusPageProps {
    stepNumber: number;
    stageOrder: ProcessStageOrder;
    title: string;
    divProps: MotionProps;
    overlayController: VeboxOverlayController;
}

export const CensusStatusPage: React.FC<CensusStatusPageProps> = ({
                                                                      stepNumber,
                                                                      stageOrder,
                                                                      title,
                                                                      divProps,
                                                                      overlayController,
                                                                  }) => {
    const {data, isLoading, error} = useCensusEnums(true);
    const [isConfirming, setIsConfirming] = React.useState(false);
    const [totalManualChanges, setTotalManualChanges] = React.useState<
        ManualChange[]
    >([]);
    const [isLoadingPage, setIsLoadingPage] = React.useState(true);
    const [errorMessage, setErrorMessage] = React.useState("");
    const divRef = React.useRef<HTMLDivElement>(null);
    const [openedSections, setOpenedSections] = React.useState<Section[]>([]);

    const {AppTranslations} = React.useContext(CensusCoolerContext);
    const {
        addOrRemoveAndMergeManualChange,
        resetConfirmedActions,
        confirmedActions,
        isLoadingActions,
        suggestedActionsAnalysisSummary,
        suggestedActionsError,
        confirmActions,
        mappedSummary,
    } = React.useContext(SuggestedActionsContext);
    const processController = useProcessController();

    const goToNext = () => {
        processController.goTo(processController.position + 1);
    };

    const reset = () => {
        resetConfirmedActions();
    };

    const closeModal = () => {
        overlayController.clear();
    };

    const confirmSelectedActions = () => {
        setIsConfirming(true);
        closeModal();
        confirmActions()
            .then(() => {
                setIsConfirming(false);
                goToNext();
            })
            .catch((error: RestApiError) => {
                manageErrors(error);
                setIsConfirming(false);
            });
    };

    const showAdviseDialog = () => {
        overlayController.show(
            <AdviseModal
                onClose={closeModal}
                onSubmit={confirmSelectedActions}
                description={
                    <p>
                        {AppTranslations.massiveActions.censusCooler.confirmSuggestions}
                    </p>
                }
            />,
        );
    };

    const manageErrors = (error: RestApiError) => {
        let descriptions: React.ReactNode[] = [];
        descriptions.push(
            <DefaultErrorBoundaryFallback
                errorMessage={error.message}
                style={{backgroundColor: "white"}}
            />,
        );
        overlayController.show(
            <MassiveOnSubmitModal
                title={AppTranslations.errorMessages.error}
                descriptions={descriptions}
                onClose={closeModal}
                width={"auto"}
            />,
        );
    };

    const toggleSections = (section: Section) => {
        setOpenedSections(toggleSection(section, openedSections));
    };

    const dataToRender = React.useMemo(() => {
        if (!isLoadingActions && data?.actions && mappedSummary?.length > 0) {
            return mappedSummary
                ?.filter(filterOpenedSections(data, openedSections))
                ?.map((summary, index) => {
                    const {
                        inventoryStatus,
                        inventoryStatusDetail,
                        inventoryStatusReason,
                        withReason,
                        count,
                        availableActions
                    } = summary;
                    return (
                        <EquipmentWithStatusSection
                            status={inventoryStatus}
                            statusDetail={inventoryStatusDetail}
                            inventoryStatusReason={inventoryStatusReason}
                            detailCount={withReason?.filter(
                                filterReasons(data, openedSections),
                            )}
                            suggestedAction={summary?.suggestedAction}
                            translatedActionsEnum={data?.actions}
                            availableActions={availableActions}
                            equipmentCount={count}
                            tableHeaderName={getTableHeadersName(
                                data,
                                inventoryStatus,
                                inventoryStatusDetail,
                            )}
                            addOrRemoveAndMergeManualChange={addOrRemoveAndMergeManualChange}
                            level={0}
                            height={"70vh"}
                            divRef={divRef}
                            index={index}
                            toggleSection={toggleSections}
                            descriptions={data?.descriptions}
                        />
                    );
                });
        }
        return [];
    }, [
        totalManualChanges,
        data?.actions,
        mappedSummary,
        isLoading,
        isLoadingPage,
        openedSections,
        isLoadingActions,
        confirmedActions
    ]);

    React.useEffect(() => {
        setTotalManualChanges(
            confirmedActions
                .map((action) => action.manualChanges)
                .reduce((a, b) => [...a, ...b], []),
        );
    }, [confirmedActions]);

    React.useEffect(() => {
        setIsLoadingPage(isLoadingActions || isLoading);
    }, [isLoading, isLoadingActions]);

    return (
        <Display isVisible={processController.position + 1 === stepNumber}>
            <motion.div key={stepNumber} {...divProps}>
                <ProcessStage
                    stepNumber={stepNumber}
                    title={title}
                    stageOrder={stageOrder}
                >
                    <div className={"flx-row"}>
                        <ProcessStageArrow
                            stepNumber={stepNumber}
                            direction={ProcessStageArrowDirection.BACKWARD}
                            visible={false}
                        />
                        <VeBoxCard className={"census-status-container"}>
                            <div className={"flx-row flx-space-between align-items-center "}>
                                <h2 className={"card-info"}>
                                    {
                                        AppTranslations.massiveActions.censusCooler
                                            .confirmSuggestions
                                    }
                                </h2>
                                <h2 className={"card-info"}>
                                    {suggestedActionsAnalysisSummary?.total ?? 0}{" "}
                                    {suggestedActionsAnalysisSummary?.total === 1
                                        ? AppTranslations.massiveActions.massiveActionsCommon
                                            .equipmentLabel
                                        : AppTranslations.massiveActions.massiveActionsCommon
                                            .equipmentLabelPlural}
                                </h2>
                            </div>
                            <Display
                                isVisible={!isLoadingPage && !(suggestedActionsError || error)}
                            >
                                <Display isVisible={dataToRender.length > 0}>
                                    <div ref={divRef} className={"accordion-container"}>
                                        <DesAccordion enableSingleSectionOpen={true}>
                                            {dataToRender}
                                        </DesAccordion>
                                    </div>
                                </Display>
                                <Display isVisible={dataToRender.length === 0}>
                                    <EmptyState>
                                        <EmptyState.InfoIcon
                                            styles={{
                                                root: {
                                                    color: "var(--empty-indicator-svg-stroke)",
                                                }
                                            }}
                                            glyph={infoIcons.NoContent}
                                        />
                                        <EmptyState.Title color="var(--empty-indicator-title-color)">{AppTranslations.noDataAvailable}</EmptyState.Title>
                                    </EmptyState>
                                </Display>
                            </Display>
                            <Display
                                isVisible={isLoadingPage && !(suggestedActionsError || error)}
                            >
                                <div className={"loader-container"}>
                                    <Loader relativelyCentered={true}/>
                                </div>
                            </Display>
                            <FormCardFooter
                                onReset={reset}
                                onNext={showAdviseDialog}
                                restButtonText={"Reset"}
                                nextButtonText={"Submit"}
                                loadable={true}
                                disabled={isConfirming || isLoadingActions}
                                disabledCancel={isConfirming || isLoadingActions}
                                loading={isConfirming}
                            />
                        </VeBoxCard>
                        <ProcessStageArrow
                            stepNumber={stepNumber}
                            direction={ProcessStageArrowDirection.FORWARD}
                            visible={false}
                        />
                    </div>
                </ProcessStage>
            </motion.div>
        </Display>
    );
};
